/**
 * useGameMessageBridge.js
 *
 * 用於管理遊戲頁面與 HiTeach 的通信橋接器
 * 包含使用者驗證、訊息監聽器和清理功能
 */

import { onMounted, onUnmounted } from 'vue';
import jwt_decode from 'jwt-decode';

/**
 * MessageBridge 工具類
 * 處理 WebView 和 Web 環境的訊息通信
 */
const MessageBridge = {
    // 環境類型
    ENV: {
        WEBVIEW: 'webview',
        WEB: 'web'
    },
    // 當前環境
    currentEnv: 'webview',
    // 初始化
    init() {
        this.currentEnv = window?.chrome?.webview ? this.ENV.WEBVIEW : this.ENV.WEB;
        return this;
    },
    // 發送消息
    postMessage(data, origin = '*') {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.postMessage(data);
        } else {
            window.postMessage(data, origin);
        }
    },
    // 添加消息監聽器
    addEventListener(callback) {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.addEventListener('message', event => {
                callback(event);
            });
        } else {
            window.addEventListener('message', event => {
                callback(event);
            });
        }
    },
    // 移除消息監聽器
    removeEventListener(callback) {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.removeEventListener('message', callback);
        } else {
            window.removeEventListener('message', callback);
        }
    }
};

/**
 * 設定使用者驗證資料
 * @param {Object} authData - 包含 id_Token, access_Token, expires_in, login_Id, school_vers 等欄位
 * @param {String} gameName - 遊戲名稱，用於 console.log 識別
 */
export const setupUserAuth = (authData, gameName = 'Game') => {
    try {
        console.log(`[${gameName}] 設定使用者驗證資料:`, authData);

        // 儲存登入資料到 localStorage（使用標準鍵值）
        if (authData.id_Token) {
            localStorage.setItem('id_token', authData.id_Token);
        }
        if (authData.access_Token) {
            localStorage.setItem('access_token', authData.access_Token);
        }
        if (authData.expires_in) {
            localStorage.setItem('expires_in', authData.expires_in);
        }

        if (authData?.hostSoftwareVersion) {
            sessionStorage.setItem('HiTeach_Version', authData.hostSoftwareVersion);
            console.log(`[${gameName}] 儲存 HiTeach 版本:`, authData.hostSoftwareVersion);
        }

        // 儲存 HiTeach 名單（如果有）
        if (authData.memberList && Array.isArray(authData.memberList)) {
            sessionStorage.setItem('ht_memberList', JSON.stringify(authData.memberList));
            console.log(`[${gameName}] 儲存 HiTeach 名單 (${authData.memberList.length} 位學生):`, authData.memberList);
        }

        // 儲存 class_id（只在有實際值時更新，避免覆蓋已有的正確值）
        if (authData.class_id !== undefined && authData.class_id !== null && authData.class_id !== '') {
            sessionStorage.setItem('ht_class_id', authData.class_id);
            console.log(`[${gameName}] 儲存 class_id:`, authData.class_id);
        } else if (authData.class_id !== undefined) {
            // 如果回應中 class_id 是空值，記錄警告但不覆蓋原有值
            console.warn(`[${gameName}] 收到空的 class_id (${authData.class_id})，保留原有值:`, sessionStorage.getItem('ht_class_id'));
        }

        // 儲存校園版授權狀態（如果有）
        if (authData.school_vers !== undefined) {
            sessionStorage.setItem('school_vers', String(authData.school_vers));
            console.log(`[${gameName}] 校園版授權狀態:`, authData.school_vers);
        }

        // 如果有 id_Token，解碼並儲存 profile
        if (authData.id_Token) {
            try {
                const decoded = jwt_decode(authData.id_Token);
                const profile = {
                    name: decoded.name || '使用者',
                    id: authData.login_Id,
                    picture: decoded.picture || ''
                };
                localStorage.setItem('profile', JSON.stringify(profile));
            } catch (decodeError) {
                console.error(`[${gameName}] 解碼 id_token 失敗:`, decodeError);
            }
        }

        // 設定 jumpFrom 標記
        sessionStorage.setItem('jumpFrom', 'HT');

        console.log(`[${gameName}] 驗證資料已儲存完成`);
    } catch (error) {
        console.error(`[${gameName}] 設定使用者驗證時發生錯誤:`, error);
    }
};

/**
 * 使用遊戲訊息橋接器的 Composable
 * @param {String} gameName - 遊戲名稱（例如：'Snake', 'Mole', 'Star'）
 * @param {Function} onInitGameHome - 收到 Init.GameHome 時的額外處理回調（可選）
 * @returns {Object} - { messageBridge, setupUserAuth }
 */
export const useGameMessageBridge = (gameName = 'Game', onInitGameHome = null) => {
    // 初始化消息橋接器
    const messageBridge = MessageBridge.init();

    // 儲存監聽器回調函數的引用，以便在 onUnmounted 時移除
    let messageHandler = null;

    onMounted(() => {
        // 定義訊息處理函數
        messageHandler = (event) => {
            const msg = event.data;

            // 監聽 Init.GameHome 訊息，更新使用者驗證資料
            if (msg.action === 'Init.GameHome') {
                console.log(`[${gameName}] 收到 Init.GameHome 訊息:`, msg);

                // 執行使用者驗證設定
                setupUserAuth(msg.data, gameName);

                // 如果有額外的處理回調，執行它
                if (onInitGameHome && typeof onInitGameHome === 'function') {
                    onInitGameHome(msg.data);
                }
            }
        };

        // 註冊監聽器
        messageBridge.addEventListener(messageHandler);
        console.log(`[${gameName}] Init.GameHome 監聽器已註冊`);
    });

    onUnmounted(() => {
        // 清理監聽器
        if (messageHandler) {
            messageBridge.removeEventListener(messageHandler);
            console.log(`[${gameName}] Init.GameHome 監聽器已清理`);
            messageHandler = null;
        }
    });

    return {
        messageBridge,
        setupUserAuth: (authData) => setupUserAuth(authData, gameName)
    };
};

export default useGameMessageBridge;
